// Pure function, no Angular DI — same convention as offline-cache-key.ts,
// offline-feature-gate.ts and offline-video-capability.ts.
//
// "Is this app running as an INSTALLED app rather than an ordinary browser tab?" Offline
// access to saved content is confined to the installed app; a regular tab keeps working
// exactly as it did before offline mode existed (streams video, no downloads hub).
//
// MUST BE ASKED OF THE TOP-LEVEL WINDOW. The `display-mode` media feature describes the
// top-level browsing context only — inside an iframe it always reports `browser`, whatever
// the app was launched as. That matters here specifically: a saved lesson's note embeds its
// video as a same-app <iframe src="/media?..."> (Quill's MediaBlot), so a naive
// matchMedia() on `window` would report "not installed" for exactly the frame that needs to
// play the saved copy. Every frame of this app is same-origin, so window.top is readable;
// the try/catch is for the theoretical cross-origin embed, where falling back to this
// window's own answer is the safe (more restrictive) result.
function topWindow(): Window {
  try {
    // Touching a property is what actually throws on a cross-origin parent, not the access
    // to .top itself — so probe one before trusting it.
    if (window.top && typeof window.top.matchMedia === 'function') return window.top;
  } catch {
    /* cross-origin embed — fall through to this window */
  }
  return window;
}

// The display modes a browser reports for an installed app. `standalone` is the one the
// school manifest asks for (ManifestJson.display, Impata.WebAPI/Models/ManifestJson.cs), but
// a browser may substitute a related mode — desktop Chrome reports
// `window-controls-overlay` when that titlebar option is on, and a user can launch an
// installed app full screen — and all of those are still "installed", not a browser tab.
const INSTALLED_DISPLAY_MODES = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'];

export function isInstalledPwa(): boolean {
  const win = topWindow();

  // iOS Safari's own pre-standard flag. Modern iOS also answers display-mode correctly, but
  // this still covers older versions that added-to-home-screen and report nothing else.
  if ((win.navigator as any)?.standalone === true) return true;

  // A TWA (Play Store wrapper) reports an android-app:// referrer and no display-mode.
  try {
    if (win.document?.referrer?.startsWith('android-app://')) return true;
  } catch {
    /* ignore */
  }

  if (typeof win.matchMedia !== 'function') return false;
  return INSTALLED_DISPLAY_MODES.some((mode) => {
    try {
      return win.matchMedia(`(display-mode: ${mode})`).matches;
    } catch {
      return false; // an unknown media feature throws in some engines rather than not matching
    }
  });
}
